import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  Grid,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  ChevronRight as ArrowIcon,
  LocalHospital as HospitalIcon,
  Restaurant as TipIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import HoverCard3D from '../components/HoverCard3D';
import QueueWidget from '../components/QueueWidget';
import CareTeam from '../components/CareTeam';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function CareSvg({ sx }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 200 120"
      sx={{ width: '100%', maxWidth: 260, ...sx }}
    >
      <defs>
        <linearGradient id="heartGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f59e0b" />
          <stop offset="100%" stopColor="#ea580c" />
        </linearGradient>
      </defs>
      <path
        d="M100 95 C60 70 30 50 30 35 C30 18 45 8 62 8 C78 8 92 18 100 32 C108 18 122 8 138 8 C155 8 170 18 170 35 C170 52 140 72 100 95Z"
        fill="url(#heartGrad)"
        opacity="0.9"
      />
      <path
        d="M100 88 C68 66 42 48 42 36 C42 24 52 16 64 16 C76 16 88 26 100 42 C112 26 124 16 136 16 C148 16 158 24 158 36 C158 50 132 68 100 88Z"
        fill="#fffefb"
        opacity="0.35"
      />
      <circle cx="100" cy="48" r="6" fill="#0f766e" opacity="0.85" />
    </Box>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tip, setTip] = useState(null);
  const [tipLoading, setTipLoading] = useState(true);

  useEffect(() => {
    fetchVisits();
    fetchHealthTip();
  }, []);

  const fetchVisits = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/v1/patient/summaries/list`);
      setVisits(response.data.visits);
    } catch (error) {
      toast.error('Failed to load visits');
    } finally {
      setLoading(false);
    }
  };

  const fetchHealthTip = async () => {
    setTipLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/v1/patient/health-tip`);
      setTip(res.data || null);
    } catch {
      setTip(null);
    } finally {
      setTipLoading(false);
    }
  };

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  return (
    <Box>
      <QueueWidget />
      <Grid container spacing={3} sx={{ mb: 4 }} alignItems="center">
        <Grid item xs={12} md={7}>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              fontWeight: 700,
              color: '#431407',
              mb: 1,
              fontSize: { xs: '2rem', md: '2.35rem' },
            }}
          >
            Your visits & summaries
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8, maxWidth: 520, fontWeight: 500 }}>
            Read what happened at each visit, follow your care plan, and see next steps — written to be clear,
            not clinical.
          </Typography>
        </Grid>
        <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 4,
              background: 'linear-gradient(160deg, rgba(255,255,255,0.95) 0%, #fff7ed 100%)',
              border: '1px solid rgba(180, 83, 9, 0.15)',
              boxShadow: '0 20px 48px -24px rgba(67, 20, 7, 0.18)',
              transition: 'transform 0.55s cubic-bezier(0.22, 1, 0.36, 1)',
              '&:hover': {
                transform: 'perspective(900px) rotateY(8deg) rotateX(-5deg) scale(1.02)',
              },
            }}
          >
            <CareSvg />
          </Box>
        </Grid>
      </Grid>

      <CareTeam />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <HoverCard3D
            cardSx={{
              background: 'linear-gradient(135deg, #fffefb 0%, #fef3c7 100%)',
              border: '1px solid rgba(180, 83, 9, 0.18)',
              height: '100%',
            }}
          >
            <CardContent sx={{ py: 2.5, px: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Avatar sx={{ bgcolor: '#b45309', width: 44, height: 44 }}>
                  <TipIcon />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ color: '#7c2d12' }}>
                      Today's healthy habit
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={fetchHealthTip}
                      disabled={tipLoading}
                      sx={{ ml: 'auto' }}
                      aria-label="Refresh tip"
                    >
                      {tipLoading ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                  {tipLoading && !tip ? (
                    <Typography variant="body2" color="text.secondary">
                      Loading a tip…
                    </Typography>
                  ) : tip ? (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#7c2d12', mb: 0.5 }}>
                        {tip.tip}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {tip.benefit}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Eat a balanced plate today — half veg, a quarter protein, a quarter whole grains.
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </HoverCard3D>
        </Grid>
      </Grid>

      {visits.length === 0 && !loading ? (
        <HoverCard3D
          cardSx={{
            background: 'linear-gradient(145deg, #fffefb 0%, #faf6f0 100%)',
            maxWidth: 560,
            mx: 'auto',
            mt: 2,
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 6, px: 4 }}>
            <HospitalIcon sx={{ fontSize: 52, color: 'primary.main', mb: 2, opacity: 0.85 }} />
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontFamily: '"Cormorant Garamond", serif', fontSize: '1.5rem' }}
            >
              When your visits are ready, they will appear here with plain-language summaries.
            </Typography>
          </CardContent>
        </HoverCard3D>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          {visits.map((visit) => (
            <HoverCard3D
              key={visit.visit_id}
              onClick={() => navigate(`/visit/${visit.visit_id}`)}
              cardSx={{
                cursor: 'pointer',
                background: 'linear-gradient(135deg, #fffefb 0%, #fff7ed 100%)',
                border: '1px solid rgba(180, 83, 9, 0.12)',
              }}
            >
              <CardContent sx={{ py: 2.5, px: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Avatar
                    sx={{
                      bgcolor: 'secondary.main',
                      width: 48,
                      height: 48,
                      boxShadow: '0 8px 20px -6px rgba(15, 118, 110, 0.45)',
                    }}
                  >
                    <CalendarIcon />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="subtitle1" fontWeight={800}>
                        {formatDate(visit.date)}
                      </Typography>
                      <Chip size="small" label={visit.reason} color="primary" variant="outlined" sx={{ fontWeight: 700 }} />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                      with {visit.doctor_name}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    aria-label="Open visit"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/visit/${visit.visit_id}`);
                    }}
                  >
                    <ArrowIcon />
                  </IconButton>
                </Box>
              </CardContent>
            </HoverCard3D>
          ))}
        </Box>
      )}
    </Box>
  );
}
